import { Col, Row } from '@opendoor/atoms/box';
import { Combobox } from '@opendoor/atoms/combobox';
import { Field } from '@opendoor/atoms/field';
import { Text } from '@opendoor/atoms/text';
import type { ErrorKey, RangeOption } from 'components/marketplace/search/types';

import { buildRangeFilterOptions, filterIntOptions } from './utils';

const MIN_PLACEHOLDER: RangeOption = { label: 'No min', value: '0' };
const MAX_PLACEHOLDER: RangeOption = { label: 'No max', value: '500000000' };

const LotSizeOptions: RangeOption[] = [
  { label: '2000 sq ft', value: '2000' },
  { label: '4000 sq ft', value: '4000' },
  { label: '6000 sq ft', value: '6000' },
  { label: '8000 sq ft', value: '8000' },
  { label: '0.25 acres', value: '10890' },
  { label: '0.5 acres', value: '21780' },
  { label: '1 acre', value: '43560' },
  { label: '2 acres+', value: '87120' },
];

export interface LotSizeFilterProps {
  minLotSize?: string | null;
  maxLotSize?: string | null;
  handleMinLotSizeChange: (value: string) => void;
  handleMaxLotSizeChange: (value: string) => void;
  handleError: (key: ErrorKey, val: string) => void; // used to notify parent component of error state
  errors?: Record<ErrorKey, string> | null;
}

export const LotSizeFilter = ({
  minLotSize,
  maxLotSize,
  handleMinLotSizeChange,
  handleMaxLotSizeChange,
  handleError,
  errors,
}: LotSizeFilterProps) => {
  const onMinLotSizeChange = (value: string) => {
    if (maxLotSize && parseInt(value, 10) > parseInt(maxLotSize, 10)) {
      handleError('minLotSize', 'Min lot size must be less than max lot size');
      return;
    }
    handleError('minLotSize', '');
    handleMinLotSizeChange(value);
  };

  const onMaxLotSizeChange = (value: string) => {
    if (minLotSize && parseInt(value, 10) < parseInt(minLotSize, 10)) {
      handleError('maxLotSize', 'Max lot size must be greater than min lot size');

      return;
    }
    handleError('maxLotSize', '');
    handleMaxLotSizeChange(value);
  };

  const minOptions = buildRangeFilterOptions(
    filterIntOptions(LotSizeOptions, 'lt', maxLotSize ?? undefined),
    MIN_PLACEHOLDER,
  );
  const maxOptions = buildRangeFilterOptions(
    filterIntOptions(LotSizeOptions, 'gt', minLotSize ?? undefined),
    MAX_PLACEHOLDER,
  );

  return (
    <Col className="w-full gap-8">
      <Text as="p" className="text-label-xl">
        Lot size
      </Text>
      <Row className="w-full justify-between">
        <Field className="w-[48%]" error={errors?.['minLotSize'] || undefined}>
          <Combobox
            aria-label="Minimum lot size"
            placeholder={MIN_PLACEHOLDER.label}
            options={minOptions}
            value={minOptions.find((option) => option.value === minLotSize) ?? null}
            error={Boolean(errors?.['minLotSize'])}
            size="sm"
            onValueChange={(option) => onMinLotSizeChange(option?.value ?? MIN_PLACEHOLDER.value)}
          />
        </Field>
        <Field className="w-[48%]" error={errors?.['maxLotSize'] || undefined}>
          <Combobox
            aria-label="Maximum lot size"
            placeholder={MAX_PLACEHOLDER.label}
            options={maxOptions}
            value={maxOptions.find((option) => option.value === maxLotSize) ?? null}
            error={Boolean(errors?.['maxLotSize'])}
            size="sm"
            onValueChange={(option) => onMaxLotSizeChange(option?.value ?? MAX_PLACEHOLDER.value)}
          />
        </Field>
      </Row>
    </Col>
  );
};
